<template>
  <v-container
    fill-height
    justify-content-start
    :class="{ 'pa-2': $vuetify.breakpoint.xsOnly }"
    class="app-section-width"
  >
    <v-flex class="app-scroll-section">
      <validation-observer ref="settingsForm" v-slot="{}">
        <v-form class="mt-4" v-if="!changePassword">
          <validation-provider
            :name="$t('fields.language')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              :label="$t('fields.language')"
              v-model="language"
              :items="allowedLanguageCodes"
              item-text="name"
              item-value="value"
              :error-messages="edit ? errors : ''"
              :outlined="!edit ? true : false"
              :readonly="!edit ? true : false"
              :filled="edit ? true : false"
              :append-icon="!edit ? '' : '$dropdown'"
            >
              <template v-slot:selection="{ item }">
                {{ languages[item].native }}
              </template>
              <template v-slot:item="{ item }">
                {{ languages[item].native }}
              </template>
            </v-select>
          </validation-provider>
          <v-autocomplete
            :label="$t('fields.display_currency')"
            v-model="displayCurrencyId"
            :items="currencies"
            item-text="currency.name"
            item-value="currency.id"
            :loading="!currencies.length || !displayCurrencyId"
            :outlined="!edit ? true : false"
            :readonly="!edit ? true : false"
            :filled="edit ? true : false"
            :append-icon="!edit ? '' : '$dropdown'"
          />

          <div class="mb-1">
            <div class="title caption grey--text">
              {{ $t("fields.time_format") }}
            </div>
            <div class="px-3">
              <v-radio-group v-model="timeFormat" :readonly="!edit ? true : false" class="mt-0" row>
                <v-radio :label="$t('time_format.AM_PM')" :value="timeFormats['AM_PM']"></v-radio>
                <v-radio :label="$t('time_format.H_24')" :value="timeFormats['H_24']"></v-radio>
              </v-radio-group>
            </div>
          </div>

          <v-select
            :label="$t('fields.first_day_of_week')"
            v-model="firstDayOfWeek"
            :items="firstDayOfWeekItems"
            item-value="id"
            item-text="text"
            :outlined="!edit ? true : false"
            :readonly="!edit ? true : false"
            :filled="edit ? true : false"
            :append-icon="!edit ? '' : '$dropdown'"
          />

          <v-btn @click="editSettings()" v-if="!edit" outlined block>
            {{ $t('actions.edit_settings') }}
          </v-btn>
          <v-btn
            @click="editPassword()"
            v-if="!edit"
            outlined
            block
            class="mt-4"
          >
            {{ $t('actions.change_password') }}
          </v-btn>
          <v-layout justify-end>
            <v-btn
              @click="cancelEditSettings()"
              v-if="edit"
              outlined
              class="mr-4"
            >
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn @click="saveSettings()" v-if="edit" outlined>
              {{ $t('actions.save') }}
            </v-btn>
          </v-layout>
          <CredentialsList v-if="!edit" :webappUser='webappUser' :webappUserCredentials='webappUserCredentials' />
        </v-form>
      </validation-observer>
      <validation-observer ref="passwordForm" v-slot="{}">
        <v-form class="mt-4" v-if="changePassword">
          <validation-provider
            v-if="hasPassword"
            :name="$t('fields.old_password')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('fields.old_password')"
              type="password"
              v-model="oldPassword"
              :error-messages="errors"
              filled
            ></v-text-field>
          </validation-provider>
          <validation-provider
            :name="$t('fields.new_password')"
            rules="required|min:6"
            v-slot="{ errors }"
            vid="password"
          >
            <v-text-field
              :label="$t('fields.new_password')"
              type="password"
              v-model="newPassword"
              :error-messages="errors"
              filled
            ></v-text-field>
          </validation-provider>
          <validation-provider
            :name="$t('fields.repeat_new_password')"
            rules="required|confirmed:password"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('fields.repeat_new_password')"
              type="password"
              v-model="repeatNewPassword"
              :error-messages="errors"
              filled
            ></v-text-field>
          </validation-provider>
          <v-layout justify-end>
            <v-btn @click="cancelEditPassword()" outlined class="mr-4">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn @click="savePassword()" outlined>
              {{ $t('actions.save') }}
            </v-btn>
          </v-layout>
        </v-form>
      </validation-observer>
      <v-chip v-if="!edit && !changePassword" class="mt-2 light-background primary--text text--lighten-1" @click="signOutDialog = true">
        <v-icon size="22" color="primary lighten-1">power_settings_new</v-icon>
        <span class="ml-2">{{ $t("actions.sign_out") }}</span>
      </v-chip>
    </v-flex>
    <v-dialog v-model="signOutDialog" persistent width="300">
      <v-card>
        <v-card-title>
          {{$t('actions.sign_out')}}
        </v-card-title>
        <v-card-text>
          {{$t('sign_out.warning')}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" text @click="signOutDialog = false">{{ $t('actions.cancel') }}</v-btn>
          <v-btn color="primary darken-1" text  @click="signOut">{{ $t('actions.accept') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapGetters, mapState } from 'vuex'
import routeNames from '../router/routes'
import router from '@/router'
import languages from '@/data/languages'
import CredentialsList from '@/components/view-components/CredentialsList'
import credentialMixins from '@/mixins/Credentials'
import { timeFormats, firstDayOfWeekOptions } from '@/config'

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    CredentialsList,
  },
  data () {
    return {
      timeFormats,
      languages,
      edit: false,
      changePassword: false,
      oldPassword: null,
      newPassword: null,
      repeatNewPassword: null,
      credential: null,
      signOutDialog: false,
    }
  },
  methods: {
    signOut () {
      this.$store.dispatch('auth/signOut').then(()=>{
        this.signOutDialog = false
        router.push({ name: routeNames.main.name })
      })
    },
    setChangePassword: function (value) {
      this.changePassword = value
    },
    editSettings () {
      this.edit = true
    },
    cancelEditSettings () {
      this.edit = false
    },
    saveSettings () {
      this.$refs.settingsForm.validate().then(success => {
        if (success) {
          const params = {
            languageCode: this.settings.webappUser.languageCode,
            displayCurrencyId: this.displayCurrencyId,
            timeFormat: this.settings.webappUser.timeFormat,
            firstDayOfWeek: this.settings.webappUser.firstDayOfWeek,
          }

          this.$store.dispatch('me/putSettings', params).then(() => {
            this.edit = false
            this.$store.commit('state/setMessage', { text: this.$t('success.settings'), color: 'success' })
          })
        }
      })
    },
    editPassword () {
      this.resetPasswordForm()
      this.changePassword = true
    },
    cancelEditPassword () {
      this.resetPasswordForm()
      this.changePassword = false
    },
    savePassword () {
      let self = this
      this.$refs.passwordForm.validate().then(success => {
        if (success) {
          this.$store.dispatch('me/putPassword', { oldPassword: this.oldPassword, newPassword: this.newPassword }).then(response => {
            self.resetPasswordForm()
            self.changePassword = false
            self.$store.commit('state/setMessage', { text: self.$t('success.change_password'), color: 'success' })
          }, () => {
            self.$store.commit('state/setMessage', { text: self.$t('error.change_password'), color: 'error' })
          })
        }
      })
    },
    resetPasswordForm () {
      this.oldPassword = null
      this.newPassword = null
      this.repeatNewPassword = null
      this.$refs.passwordForm.reset()
    },
  },
  computed: {
    ...mapState({
      currencies: state => state.me.currencies,
      settings: state => state.me.settings,
    }),
    ...mapGetters('me', ['allowedLanguageCodes', 'defaultLanguageCode']),
    ...mapGetters({
      getDashboardName: 'service/getDashboardName',
    }),
    hasPassword () {
      return this.credential.hasPassword
    },
    language: {
      get () {
        if (this.settings.webappUser.languageCode === undefined || this.settings.webappUser.languageCode === null) {
          return this.defaultLanguageCode
        }

        var languageCode = this.settings.webappUser.languageCode
        return languageCode
      },
      set (value) {
        this.settings.webappUser.languageCode = value
      },
    },
    displayCurrencyId: {
      get () {
        return this.settings.webappUser.displayCurrencyId || (this.$store.state.me.facility ? this.$store.state.me.facility.baseCurrencyId : null)
      },
      set (value) {
        this.settings.webappUser.displayCurrencyId = value
      },
    },
    timeFormat: {
      get () {
        return this.settings.webappUser.timeFormat
      },
      set (value) {
        this.settings.webappUser.timeFormat = value
      },
    },
    firstDayOfWeek: {
      get () {
        return this.settings.webappUser.firstDayOfWeek
      },
      set (value) {
        this.settings.webappUser.firstDayOfWeek = value
      },
    },
    firstDayOfWeekItems () {
      const days = Object.values(firstDayOfWeekOptions).map((value) => ({
        id: value,
        text: this.$t(`first_day_of_week.${ value }`),
      }))

      return days
    },
    webappUser:{
      get () {
        return this.settings.webappUser || false
      },
    },
    webappUserCredentials: {
      get () {
        return this.settings.webappUserCredentials || false
      },
    }, 
  },
  created () {
    this.$store.dispatch('me/getSettings').then((settings) => {
      this.credential = this.getCredentialFromToken(settings.webappUserCredentials)
    }).catch(() => {})
  },
  mixins: [ credentialMixins ],
}
</script>

<style lang="scss">
#settings {
  max-width: 430px !important;
  padding: 15px 15px 0 15px;

  .v-toolbar__content {
    padding: 0;
  }
}

#content {
  overflow-y: auto;
}
.v-btn--outlined {
    border: thin solid #DCDCDC;
}
</style>
